/**
 * MobileMenuSheet - Menu mobile em forma de sheet
 * 
 * Melhorias:
 * ✅ Hook useNotification centralizado
 * ✅ Código comentado removido
 * ✅ SubMenuItem component reutilizado
 * ✅ Sem lógica ternária aninhada
 */

'use client';

import * as React from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/atoms/sheet';
import { ScrollArea } from '@/components/atoms/scroll-area';
import Link from 'next/link';
import Icon from '@/components/atoms/Icons';
import { ICMSMenu } from '@/services/types';
import { useNotification } from './useNotification';
import { HEADER_COLORS } from './headerColors';

interface MobileMenuSheetProps {
  menuGroups?: ICMSMenu[];
  isWhiteTheme?: boolean;
  logoSrc?: string;
  logoAlt?: string;
}

const NOTIFICATION_MESSAGE = '⚠️ Serviço brevemente disponível.';
const NOTIFICATION_DURATION = 3000;

export function MobileMenuSheet({
  menuGroups,
  isWhiteTheme,
}: MobileMenuSheetProps) {
  const [open, setOpen] = React.useState(false);
  const { isVisible, show } = useNotification(
    NOTIFICATION_MESSAGE,
    NOTIFICATION_DURATION
  );

  const handleLinkClick = () => {
    setOpen(false);
  };

  const handleUnavailableClick = () => {
    show();
    handleLinkClick();
  };

  const menuIconColor = isWhiteTheme
    ? HEADER_COLORS.text.light
    : HEADER_COLORS.text.dark;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger
        className="inline-flex items-center justify-center p-3 xl:hidden z-50 rounded-md hover:bg-gray-100/10 transition-colors border border-gray-300/20"
        aria-label="Abrir menu"
        style={{ color: menuIconColor }}
      >
        <Icon name="menu" color={menuIconColor} className="w-7 h-7" style={{ strokeWidth: 3 }} />
      </SheetTrigger>

      {/* Notificação de serviço indisponível */}
      {isVisible && (
        <div className="fixed top-4 right-4 bg-yellow-100 text-orange-600 px-4 py-2 rounded shadow z-50 transition-all duration-300">
          {NOTIFICATION_MESSAGE}
        </div>
      )}

      <SheetContent side="top" className="w-full p-0 z-50 mt-16">
        <SheetHeader>
          <SheetTitle className="text-lg font-bold sr-only">Menu</SheetTitle>
        </SheetHeader>

        <ScrollArea maxH="76vh" className="pl-4 pt-10">
          <div className="pb-4">
            {menuGroups?.map((group, idx) => (
              <div key={`menu-group-${idx}`} className="py-6">
                {/* Sempre mostra o label, com ou sem link */}
                {group.url ? (
                  <Link
                    href={group.url}
                    className="block py-2 text-base font-semibold text-main-black hover:text-primary"
                    onClick={handleLinkClick}
                  >
                    {group.label}
                  </Link>
                ) : (
                  <p className="py-2 text-base font-semibold text-main-black">
                    {group.label}
                  </p>
                )}

                {/* Renderiza submenus se existirem */}
                {group.subMenu && group.subMenu.length > 0 && (
                  <div className="flex flex-col gap-4 mt-2">
                    {group.subMenu && group.subMenu.length > 0 ? (
                      <div className="flex flex-col gap-6">
                        {group.subMenu.map((menu, menuIdx) => (
                          <div
                            key={`submenu-${idx}-${menuIdx}-${menu.title || menuIdx}`}
                            className="space-y-3"
                          >
                            {menu.title && (
                              <p className="text-main-black text-sm font-bold uppercase tracking-wide" style={{ color: '#377396' }}>
                                {menu.title}
                              </p>
                            )}
                            {menu.subMenu && menu.subMenu.length > 0 ? (
                              <ul className="space-y-2 pl-0">
                                {menu.subMenu.map((sub, subIdx) => (
                                  <li
                                    key={`item-${idx}-${menuIdx}-${subIdx}-${sub.label}`}
                                  >
                                    {sub.url ? (
                                      sub.external_link ? (
                                        <a
                                          href={sub.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="block text-sm text-gray-600 hover:text-main-black transition-colors"
                                          onClick={handleLinkClick}
                                        >
                                          {sub.label}
                                        </a>
                                      ) : (
                                        <Link
                                          href={sub.url}
                                          className="block text-sm text-gray-600 hover:text-main-black transition-colors"
                                          onClick={handleLinkClick}
                                        >
                                          {sub.label}
                                        </Link>
                                      )
                                    ) : (
                                      <span
                                        className="block text-sm text-gray-600 cursor-pointer hover:text-main-black transition-colors"
                                        onClick={handleUnavailableClick}
                                      >
                                        {sub.label}
                                      </span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            ))}

            {/* Portal Kre+ Button no Mobile Menu */}
            <div className="py-8 border-t border-gray-200 pr-4">
              <Link
                href="#"
                className="flex items-center justify-center px-4 py-2 rounded-full text-sm font-semibold transition-all w-50 cursor-pointer"
                style={{
                  backgroundColor: HEADER_COLORS.primary.teal,
                  color: 'white',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
                onClick={handleLinkClick}
              >
                Portal Kre+
              </Link>
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}